/**
 * Where the demand for a booking week came from — the platform, mailbox or
 * manual work request it was won through.
 *
 * **Not a time report row's `source`.** That is *how the hours were reported*;
 * this is *where the work came from*. A work request sourced from Bemlo can
 * have its hours arrive on paper, and that is the ordinary case rather than an
 * anomaly — see ADR-0008. Two questions, two fields, two vocabularies.
 */

/** The filter value that means "nobody attributed this week". */
export const EMPTY_DEMAND_SOURCE = "Empty";

/**
 * The demand sources the CRM offers in the time report view's platform filter.
 *
 * **A display list, not a validation list.** `demandSource` is stored as a free
 * string because the vocabulary belongs to the booking export queue and grows
 * with every adapter ERP gains; validating against a CRM enum would reject a
 * whole import over a value the CRM had not heard of yet. The consequence is
 * the honest one: a channel ERP adds tomorrow imports fine and shows on the row
 * immediately, and only *offering it in this dropdown* waits for a CRM release.
 *
 * If that lag starts to bite, the fix is a query for the distinct values
 * actually present rather than a longer hardcoded list.
 */
export const DEMAND_SOURCE_FILTER_OPTIONS: readonly string[] = [
  "bemlo",
  "medlo",
  "inkopio-gavleborg",
  "inkopio-skane",
  "avropsplatsen",
  "nemo",
  "email",
  "upload",
  "manual",
  EMPTY_DEMAND_SOURCE
];

/**
 * How a stored `demandSource` reads in the UI.
 *
 * The stored values are the intake pipeline's own tokens — lowercase, and
 * Inköpio arrives once per region because it runs two domains. They are stored
 * verbatim so a filter is an exact match rather than a fuzzy one, and prettied
 * only at the point of display. A token this map has never seen falls back to
 * itself, so an ERP-side addition shows up as a slightly ugly label rather than
 * as a blank cell.
 */
const DEMAND_SOURCE_LABELS: Record<string, string> = {
  bemlo: "Bemlo",
  medlo: "Medlo",
  "inkopio-gavleborg": "Inköpio (Gävleborg)",
  "inkopio-skane": "Inköpio (Skåne)",
  avropsplatsen: "Avropsplatsen",
  nemo: "Nemo",
  email: "E-post",
  upload: "Uppladdning",
  manual: "Manuell"
};

export function demandSourceLabel(source: string): string {
  return DEMAND_SOURCE_LABELS[source] ?? source;
}
